import React from "react";
import { AbsoluteFill, Sequence, interpolate } from "remotion";
import type { FocusEffect, FocusMode, Rect } from "@veyso/core";
import { easingFor } from "./easing.js";
import { AnimatedCursor } from "./AnimatedCursor.js";

export interface ActiveFocus {
  effect: FocusEffect;
  /** 0 at rest, 1 at full effect. */
  amount: number;
  /** Frames since the effect began. */
  localFrame: number;
}

/**
 * Every effect covering this frame, with how far into each one we are.
 *
 * It used to return the first and stop, on the grounds that the planner
 * guaranteed effects never overlapped. That was true while they were computed
 * from detected actions; it stopped being true the moment the effects lane
 * became something you could edit, and the cost was silent — a highlight
 * stacked over an earlier zoom simply never drew, with nothing to say why.
 *
 * The envelope ramps in, holds, then returns to the untouched frame, which is
 * what keeps a zoom from snapping back.
 */
export function activeFocusEffects(effects: FocusEffect[], fps: number, frame: number): ActiveFocus[] {
  const active: ActiveFocus[] = [];

  for (const effect of effects) {
    const start = Math.round(effect.start * fps);
    const total = Math.max(1, Math.round(effect.duration * fps));
    if (frame < start || frame >= start + total) continue;

    const localFrame = frame - start;

    // Asked-for ramps, shrunk in proportion when the two together are longer
    // than the effect. Sharing the shortfall keeps a lopsided pair lopsided
    // rather than letting whichever is written first eat all the room.
    const wantIn = Math.max(0, effect.easeIn) * fps;
    const wantOut = Math.max(0, effect.easeOut) * fps;
    const squeeze = wantIn + wantOut > total ? total / (wantIn + wantOut) : 1;
    const rampIn = Math.round(wantIn * squeeze);
    const rampOut = Math.round(wantOut * squeeze);

    // Zero on both ends is a hard cut, and an interpolate over a degenerate
    // range is an exception rather than a shape.
    const holdFrom = Math.max(rampIn, total - rampOut);
    const amount =
      rampIn <= 0 && rampOut <= 0
        ? 1
        : interpolate(
            localFrame,
            // Nudged apart where a ramp is zero, so the range stays strictly
            // increasing however the two are set.
            [0, Math.max(rampIn, 0.001), Math.max(holdFrom, rampIn + 0.002), total],
            [rampIn > 0 ? 0 : 1, 1, 1, rampOut > 0 ? 0 : 1],
            {
              extrapolateLeft: "clamp",
              extrapolateRight: "clamp",
              easing: easingFor(effect.easing),
            },
          );
    active.push({ effect, amount, localFrame });
  }

  return active;
}

/**
 * The one effect allowed to move the camera.
 *
 * Overlays composite — a dim, an outline and a cursor can all be on screen at
 * once — but two zooms cannot: they would fight over one transform and the
 * result is a scale nobody asked for. The last one wins, which matches the
 * timeline, where a clip added later sits on the row above.
 */
export function activeFocus(effects: FocusEffect[], fps: number, frame: number): ActiveFocus | null {
  const zooming = activeFocusEffects(effects, fps, frame).filter((entry) => entry.effect.mode === "zoom");
  return zooming[zooming.length - 1] ?? null;
}

/**
 * The middle of what an effect points at, or null when it points at nothing.
 *
 * An un-aimed effect draws nothing at all rather than falling back to the
 * centre of the frame: a zoom into the middle of a screen recording is worse
 * than no zoom, and doing it silently is exactly how every mode came to look
 * like it ignored the region it was given.
 */
const centre = (effect: FocusEffect) =>
  effect.target
    ? { x: effect.target.x + effect.target.width / 2, y: effect.target.y + effect.target.height / 2 }
    : null;

/**
 * The transform applied to the BODY content so the target sits in the middle of
 * the frame. The centre is clamped so scaling never exposes an empty edge, and
 * the source video is only ever transformed — never cropped — so the original
 * recording stays intact.
 */
export function focusTransform(active: ActiveFocus | null): React.CSSProperties {
  if (!active || active.effect.mode !== "zoom") return {};

  const scale = 1 + (active.effect.zoom - 1) * active.amount;
  if (scale <= 1.0001) return {};

  const target = centre(active.effect);
  if (!target) return {};
  const halfViewport = 0.5 / scale;
  const cx = Math.min(1 - halfViewport, Math.max(halfViewport, target.x));
  const cy = Math.min(1 - halfViewport, Math.max(halfViewport, target.y));

  const tx = -(cx - 0.5) * scale * 100;
  const ty = -(cy - 0.5) * scale * 100;

  return {
    transform: `translate(${tx.toFixed(4)}%, ${ty.toFixed(4)}%) scale(${scale.toFixed(4)})`,
    transformOrigin: "50% 50%",
  };
}

const pct = (value: number) => `${(value * 100).toFixed(4)}%`;

/** Spotlight, highlight, callout and cursor, drawn in the content's own space. */
/**
 * How blurred the region is at a given point through the effect.
 *
 * Full radius from the first frame to the last, and nothing outside the window.
 * `amount` — the eased 0→1 every other overlay fades with — is used only to ask
 * whether the effect is running at all. A redaction that eases is a redaction
 * that shows its contents twice.
 */
/**
 * Whether an effect draws anything at this point in its envelope.
 *
 * Everything here ramps from nothing, so at `amount` zero there is a fully
 * transparent outline and a dim of zero to skip — real work for no pixels.
 *
 * A blur is the exception, and the exception matters. Its envelope starts at
 * zero like every other, but it is not fading in: it is either covering the
 * region or it is not. Skipping it on that first frame left exactly one frame
 * of the thing it exists to hide on screen — measured at 33ms, and a frame
 * anyone can pause on.
 */
export function overlayIsVisible(mode: FocusMode, amount: number): boolean {
  return mode === "blur" ? true : amount > 0.001;
}

/**
 * Where the two halves of a blurred region go.
 *
 * A blur is the whole frame drawn a second time, blurred, and clipped to a
 * rectangle — `backdrop-filter` would have been one div, and the browser's
 * exporter cannot draw it, so a region blurred to hide something exported
 * legible.
 *
 * The cost of a copy is that it has to be aligned. The outer box is the
 * region. The inner is the *whole frame* at the size it would be if the region
 * were the viewport, shifted back so the part showing through the hole is the
 * part that was there anyway.
 *
 * Its own function because getting it wrong is worse than not blurring: the
 * region covers the right area with the wrong pixels, which looks deliberate.
 */
/**
 * The dim, as everything except one rounded rectangle.
 *
 * It was `box-shadow: 0 0 0 9999px` — a spread so large it fills the frame
 * outwards from the hole. One element, one alpha, and no seams to line up at
 * fractional pixels, which is why it was chosen over four panels around the
 * region.
 *
 * The browser's exporter does not draw it. Not "does not support it" in a
 * vague way: `parse-shadow.ts` reads the offset, the offset and the blur, and
 * never reads the fourth value. The spread is parsed into an array and
 * discarded, so the shadow becomes zero-sized and paints nothing — the
 * highlight ring appears and the darkening around it does not, silently.
 *
 * `clip-path: path(evenodd, …)` it does draw: `ctx.clip(path2d, fillRule)`,
 * which is Canvas's own. Two subpaths — the whole box, then the region — and
 * `evenodd` makes the second a hole. That keeps what the spread shadow was
 * chosen for: one element, one alpha, no seams.
 *
 * Pixels rather than percentages, because that is what the exporter's path
 * parser takes — it hands `d` straight to `Path2D` and only offsets it. Which
 * is why this needs the box's size and the caller has to supply it.
 */
export function dimClipPath(box: { width: number; height: number }, target: Rect, radius = 10): string {
  const x = target.x * box.width;
  const y = target.y * box.height;
  const w = target.width * box.width;
  const h = target.height * box.height;

  // Never more than half the shorter side, or the arcs cross and the hole
  // turns inside out.
  const r = Math.max(0, Math.min(radius, w / 2, h / 2));

  const outer = `M0 0 H${round(box.width)} V${round(box.height)} H0 Z`;

  // Clockwise from the top-left arc's end, so the winding is opposite to the
  // outer rectangle — which is what `evenodd` needs to read it as a hole.
  const hole =
    `M${round(x + r)} ${round(y)} ` +
    `H${round(x + w - r)} A${round(r)} ${round(r)} 0 0 1 ${round(x + w)} ${round(y + r)} ` +
    `V${round(y + h - r)} A${round(r)} ${round(r)} 0 0 1 ${round(x + w - r)} ${round(y + h)} ` +
    `H${round(x + r)} A${round(r)} ${round(r)} 0 0 1 ${round(x)} ${round(y + h - r)} ` +
    `V${round(y + r)} A${round(r)} ${round(r)} 0 0 1 ${round(x + r)} ${round(y)} Z`;

  return `path(evenodd, "${outer} ${hole}")`;
}

/** Three decimals is well under a pixel and keeps the path readable. */
const round = (value: number): number => Number(value.toFixed(3));

export function blurRegionStyle(target: Rect): { clip: React.CSSProperties; copy: React.CSSProperties } {
  return {
    clip: {
      position: "absolute",
      left: `${target.x * 100}%`,
      top: `${target.y * 100}%`,
      width: `${target.width * 100}%`,
      height: `${target.height * 100}%`,
      borderRadius: 10,
      overflow: "hidden",
    },
    copy: {
      position: "absolute",
      left: `${(-target.x / target.width) * 100}%`,
      top: `${(-target.y / target.height) * 100}%`,
      width: `${(1 / target.width) * 100}%`,
      height: `${(1 / target.height) * 100}%`,
    },
  };
}

export function blurRadius(radius: number, amount: number): number {
  // `>= 0`, not `> 0`. On the effect's first frame the ease has not started, so
  // `amount` is exactly zero — and that is the frame the region most needs to
  // be covered on, not the one it may be uncovered on.
  return amount >= 0 && amount <= 1 ? radius : 0;
}

/**
 * `box` is the overlay's own size in pixels, which the dim needs.
 *
 * The exporter's clip-path parser takes a path in pixels — it hands `d`
 * straight to `Path2D` — so the one thing this cannot work out for itself is
 * how big it is. The section supplies it, because the section is what decided.
 */
export const FocusOverlay: React.FC<{
  active: ActiveFocus[];
  fps: number;
  box: { width: number; height: number };
}> = ({ active, fps, box }) => (
  <>
    {active.map((entry) => (
      <OneOverlay key={entry.effect.id} active={entry} fps={fps} box={box} />
    ))}
  </>
);

const OneOverlay: React.FC<{
  active: ActiveFocus;
  fps: number;
  box: { width: number; height: number };
}> = ({ active, fps, box }) => {
  const { effect, amount } = active;
  if (!overlayIsVisible(effect.mode, amount)) return null;
  const target = effect.target;
  const target_centre = centre(effect);
  if (!target || !target_centre) return null;

  return (
    <AbsoluteFill style={{ pointerEvents: "none" }}>
      {/* Dimming is a setting now, not a mode. Zero draws nothing, which is
          how a plain highlight and the old spotlight became one effect.

          A rectangular cutout, not a vignette. The soft ellipse this used to
          draw spilled far past the region and faded to black at the frame
          edges, so what it actually pointed at was ambiguous — the lit area
          was several times the size of the thing being highlighted. A hard
          edge on the region itself says exactly what is being shown.

          One element rather than four panels around the region: a spread
          box-shadow fills the whole frame from the hole outwards, so there are
          no seams to line up at fractional pixels. */}
      {effect.dim > 0 && (
        <AbsoluteFill
          style={{
            background: `rgba(0,0,0,${(effect.dim * amount).toFixed(4)})`,
            // Everything except the region. See `dimClipPath` for why this is
            // not the spread box-shadow it was: the exporter parses a
            // shadow's spread and then never reads it, so the darkening
            // silently did not appear. The radius matches the highlight
            // outline, so the two line up exactly when an effect draws both.
            clipPath: dimClipPath(box, target),
          }}
        />
      )}

      {/* The blur itself is drawn by BODY, not here.
          It needs a second copy of the footage — `backdrop-filter` is the one
          div this used to be, and the browser's exporter cannot draw it, so a
          region blurred to hide a customer name exported legible. Only the
          section that has the clips can draw them again, so that is where it
          moved. `blurRadius` still lives here, beside the envelope that
          decides it. */}

      {(effect.mode === "highlight" || effect.mode === "callout") && (
        <div
          style={{
            position: "absolute",
            left: pct(target.x),
            top: pct(target.y),
            width: pct(target.width),
            height: pct(target.height),
            border: `${Math.max(2, 4 * amount)}px solid rgba(125,211,252,${amount})`,
            borderRadius: 10,
            boxShadow: `0 0 ${28 * amount}px rgba(125,211,252,${0.55 * amount})`,
          }}
        />
      )}

      {effect.mode === "callout" && effect.label && (
        <div
          style={{
            position: "absolute",
            left: pct(target.x),
            top: `calc(${pct(target.y)} - 62px)`,
            maxWidth: "40%",
            padding: "10px 16px",
            borderRadius: 10,
            background: "rgba(15,23,42,0.94)",
            color: "#f8fafc",
            fontSize: 26,
            fontFamily: "Inter, system-ui, sans-serif",
            fontWeight: 600,
            opacity: amount,
            transform: `translateY(${(1 - amount) * 10}px)`,
            border: "1px solid rgba(125,211,252,0.4)",
          }}
        >
          {effect.label}
        </div>
      )}

      {(effect.mode === "cursor" || (effect.mode === "zoom" && effect.cursor)) && (
        // Nested in a Sequence so the cursor's own clock starts when the effect
        // does, rather than when the BODY section did.
        <Sequence
          from={Math.round(effect.start * fps)}
          durationInFrames={Math.max(1, Math.round(effect.duration * fps))}
          layout="none"
          name={`cursor-${effect.id}`}
        >
          <AnimatedCursor
            from={effect.cursorFrom ?? { x: 0.5, y: 0.85 }}
            to={target_centre}
            travelFrames={Math.round(fps * 0.45)}
            click={effect.click}
            // Counter-scale so the pointer keeps a constant on-screen size while
            // the content behind it is zoomed.
            scale={1 / (1 + (effect.zoom - 1) * amount)}
          />
        </Sequence>
      )}
    </AbsoluteFill>
  );
};
